import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DetailBox, Table, TitleBar, Total } from '../../common';
import {
  PO_DETAILS_COLUMN,
  TITLES,
  ROUTES,
  BUTTON,
  POPUP_TYPE,
  DATA_FIELD,
  LOCAL_STORAGE,
} from '../../../constants';
import {
  poDetailsCompiler,
  poDetailsBox,
  toApiDate,
  popup,
  getLocalStorageItem,
} from '../../../utils';
import { Button } from '../../shared';
import { createBill, vendorBill } from '../../../service';
import { useDebounce } from '../../../hooks';
import { reducedCalc } from '../../../utils';

export const PoDetails = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { state } = routerLocation;
  const data = poDetailsCompiler(state?.order_line);
  const [value, setValue] = useState('');
  const [editBill, setEditBill] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [isBillCreationLoading, setIsBillCreationLoading] = useState(false);
  const [showCheckBill, setShowCheckBill] = useState(false);
  const [isCheckBillLoading, setIsCheckBillLoading] = useState(false);
  const [createdBillData, setCreatedBillData] = useState(null);

  const qty = reducedCalc(data, DATA_FIELD.RECEIVED);
  const vendor_type = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);

  let filteredCol =
    vendor_type !== 'fabric'
      ? PO_DETAILS_COLUMN.filter(
        (col) =>
          col.name !== 'Rolls' &&
          !(
            (vendor_type === 'finished_goods' || vendor_type === 'yarn') &&
            col.name === 'Produced'
          )
      )
      : PO_DETAILS_COLUMN;

  // Function to determine the majority purchase_method
  const getMajorityPurchaseMethod = () => {
    const methodCount = data.reduce((acc, item) => {
      acc[item.purchase_method] = (acc[item.purchase_method] || 0) + 1;
      return acc;
    }, {});

    return Object.keys(methodCount).reduce((a, b) => (methodCount[a] > methodCount[b] ? a : b));
  };

  // Determine the majority purchase_method
  const majorityMethod = getMajorityPurchaseMethod();

  // Conditions based on the majority method
  const shouldShowAddBillButton =
    ((majorityMethod === 'produce' && data.some((item) => item.produced_qty > 0)) ||
      (majorityMethod === 'receive' && data.some((item) => item.received > 0)) ||
      majorityMethod === 'purchase') &&
    state?.invoice_status !== 'invoiced';

  const handleBillCreation = () => {
    setIsBillCreationLoading(true);
    createBill({
      data: { purchase_id: state?.id, bill_date: toApiDate(value) },
    })
      .then((response) => {
        setIsBillCreationLoading(false);
        if (!response) return;
        popup(POPUP_TYPE.SUCCESS, response.message);
        const bill = response?.data?.[0] || response?.data || null;
        setCreatedBillData(bill);
        setEditBill(false);
        setShowSave(false);
        setValue('');
        navigate(routerLocation.pathname, {
          state: { ...state, invoice_status: 'invoiced', createBill: false },
          replace: true,
        });
      })
      .catch(() => setIsBillCreationLoading(false));
  };

  const AddDateField = (bool) => {
    state.createBill = bool;
    setEditBill(bool);
  };

  useEffect(() => {
    if (!state) navigate(ROUTES.PURCHASE_ORDER);
    if (value) {
      setShowSave(true);
    }
    if (state?.invoice_status === 'invoiced') {
      setShowCheckBill(true);
    }
  }, [value, state]);

  const handleCheckBill = useDebounce(() => {
    if (createdBillData?.name) {
      navigate(`${ROUTES.BILLS}/${createdBillData.name.replaceAll('/', '_')}`, {
        state: createdBillData,
      });
      return;
    }
    setIsCheckBillLoading(true);
    vendorBill({
      data: { search: { invoice_origin: state?.name }, page_number: 1 },
    })
      .then((response) => {
        setIsCheckBillLoading(false);
        if (response?.data?.records?.length > 0) {
          navigate(`${ROUTES.BILLS}/${response.data.records[0].name.replaceAll('/', '_')}`, {
            state: response.data.records[0],
          });
        }
      })
      .catch((error) => {
        setIsCheckBillLoading(false);
        popup(POPUP_TYPE.ERR, error.message);
      });
  }, 1000);

  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.PURCHASE_ORDER, route: ROUTES.PURCHASE_ORDER },
          { text: editBill ? TITLES.CREATE_BILL : state?.name, route: ROUTES.NULL },
        ]}
      />
      <div className="scroll-div">
        <div className="d-flex justify-content-end gap-2 mb-3">
          {shouldShowAddBillButton && !editBill && !showCheckBill && vendor_type !== 'clothing' && (
            <Button
              clickHandler={() => AddDateField(true)}
              className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
              {BUTTON.CREATE_BILL}
            </Button>
          )}
          {showCheckBill && !editBill && (
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${isCheckBillLoading ? 'disabled' : ''}`}
              disabled={isCheckBillLoading}
              clickHandler={() => {
                setIsCheckBillLoading(true);
                handleCheckBill();
              }}>
              {isCheckBillLoading && (
                <span
                  className="spinner-border spinner-border-sm text-white me-2"
                  role="status"
                />
              )}
              {isCheckBillLoading ? 'Checking...' : 'Check Bill'}
            </Button>
          )}
          {editBill && (
            <Button
              clickHandler={() => AddDateField(false)}
              className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
              {BUTTON.CANCEL}
            </Button>
          )}
        </div>
        <DetailBox
          colCount={2}
          colData={state}
          dataCompiler={poDetailsBox}
          value={value}
          setValue={setValue}
          clickHandler={handleBillCreation}
          showSave={showSave}
          isBillCreationLoading={isBillCreationLoading}
        />
        <Table columns={filteredCol} initialData={data} PAGE_SIZE={4} colFilter={false} />
        <Total
          qty={qty}
          total={state?.amount_total}
          uom={data[0]?.uom}
          currency_code={state?.currency_id.name}
        />
      </div>
    </>
  );
};
